<template>
  <div v-if="videoData" class="details-container">
    <ResponseMessage :type="responseType">
      <span v-html="responseMessage"></span>
    </ResponseMessage>

    <div class="details-header">
      <h2>Video Publishing Preview</h2>

      <div class="details-header-buttons" v-if="canShareVideo(organizationId)">
        <button 
          v-if="shareLink" 
          class="cancel-button medium" 
          @click="unshareVideo"
        >
          Unshare Video
        </button>
        <button 
          v-if="shareLink" 
          class="important-button medium" 
          @click="shareVideo"
        >
          Copy Share Link
        </button>
        <button 
          v-if="!shareLink" 
          class="important-button medium" 
          @click="shareVideo"
        >
          Share Video
        </button>
      </div>
    </div>

    <VideoHero :video="videoData" />
    
    <div class="details-content">
      <div class="details-section">
        <h3>Information</h3>
        <div class="details-group">
          <label>Title</label>
          <div class="details-value">{{ videoData.title }}</div>
        </div>

        <div class="details-group">
          <label>Logline</label>
          <div class="details-value">{{ videoData.logline }}</div>
        </div>

        <div class="details-group">
          <label>Description</label>
          <div class="details-value">{{ videoData.description }}</div>
        </div>

        <div class="details-group half-width">
          <label>Languages</label>
          <div class="details-value">{{ videoData.languages }}</div>
        </div>

        <div class="details-group half-width">
          <label>Subtitles</label>
          <div class="details-value">{{ videoData.subtitles }}</div>
        </div>

        <div class="details-group full-width">
          <label>Festival Selections</label>
          <div class="details-value">{{ videoData.awards }}</div>
        </div>
      </div>

      <div class="details-section">
        <h3>Production</h3>
        <div class="details-group half-width">
          <label>First Release Date</label>
          <div class="details-value">{{ formatDate(videoData.release_date) }}</div>
        </div>

        <div class="details-group half-width">
          <label>Production Companies</label>
          <div class="details-value">{{ videoData.production_company }}</div>
        </div>

        <div class="details-group half-width">
          <label>Directors</label>
          <div class="details-value">{{ videoData.directors }}</div>
        </div>

        <div class="details-group half-width">
          <label>Writers</label>
          <div class="details-value">{{ videoData.writers }}</div>
        </div>

        <div class="details-group full-width">
          <label>Stars</label>
          <div class="details-value">{{ videoData.starring }}</div>
        </div>
      </div>

      <div class="details-section">
        <h3>Properties</h3>
        <div class="details-group half-width">
          <label>Category</label>
          <div class="details-value">{{ getCategoryDisplay(videoData.category) }}</div>
        </div>

        <div class="details-group half-width">
          <label>Format</label>
          <div class="details-value">{{ getFormatDisplay(videoData.format) }}</div>
        </div>

        <div class="details-group half-width">
          <label>ESRB Content Rating</label>
          <div class="details-value">{{ getContentRatingDisplay(videoData.content_rating) }}</div>
        </div>

        <div class="details-group half-width">
          <label>Motion Amount</label>
          <div class="details-value">{{ getMotionDisplay(videoData.motion_amount) }}</div>
        </div>
      </div>

      <div v-if="videoData.video_file_id" class="details-section">
        <h3>Video</h3>
        <div class="details-group full-width">
          <div class="details-value">
            <VideoFileStatus :video-file="videoData.video_file" />
          </div>
        </div>
      </div>

      <div v-if="videoData.trailer_file_id" class="details-section">
        <h3>Trailer</h3>
        <div class="details-group full-width">
          <div class="details-value">
            <VideoFileStatus :video-file="videoData.trailer_file" />
          </div>
        </div>
      </div>

      <div class="details-section">
        <h3>Assets</h3>
        <div class="details-group half-width">
          <label>Hero Art</label>
          <div class="details-value">
            <ImageThumbnail v-if="videoData.banner_url" :url="videoData.banner_url" />
            <span v-else>No hero art uploaded</span>
          </div>
        </div>

        <div class="details-group half-width">
          <label>Poster</label>
          <div class="details-value">
            <ImageThumbnail v-if="videoData.poster_url" :url="videoData.poster_url" />
            <span v-else>No poster uploaded</span>
          </div>
        </div>
      </div>

      <div v-if="isSuperuser()" class="details-section">
        <h3>Unused Assets</h3>
        <div class="details-group third-width">
          <label>Screenshot 1</label>
          <div class="details-value">
            <ImageThumbnail v-if="videoData.screenshot1_url" :url="videoData.screenshot1_url" />
            <span v-else>No screenshot uploaded</span>
          </div>
        </div>

        <div class="details-group third-width">
          <label>Screenshot 2</label>
          <div class="details-value">
            <ImageThumbnail v-if="videoData.screenshot2_url" :url="videoData.screenshot2_url" />
            <span v-else>No screenshot uploaded</span>
          </div>
        </div>

        <div class="details-group third-width">
          <label>Screenshot 3</label>
          <div class="details-value">
            <ImageThumbnail v-if="videoData.screenshot3_url" :url="videoData.screenshot3_url" />
            <span v-else>No screenshot uploaded</span>
          </div>
        </div>

        <div class="details-group half-width">
          <label>Thumbnail</label>
          <div class="details-value">
            <ImageThumbnail v-if="videoData.thumbnail_url" :url="videoData.thumbnail_url" />
            <span v-else>No thumbnail uploaded</span>
          </div>
        </div>
      </div>

      <div v-if="isSuperuser()" class="details-section">
        <h3>Admin</h3>
        <div class="details-group">
          <label>Visibility</label>
          <div class="details-value">{{ getVisibilityDisplay(videoData.visibility) }}</div>
        </div>
        
        <div class="details-group">
          <label>Review Status</label>
          <div class="details-value">{{ getReviewStatusDisplay(videoData.review) }}</div>
        </div>

        <div class="details-group half-width">
          <label>Availability Start Date</label>
          <div class="details-value">{{ formatDate(videoData.avail_start_date) }}</div>
        </div>

        <div class="details-group half-width">
          <label>Availability End Date</label>
          <div class="details-value">{{ formatDate(videoData.avail_end_date) }}</div>
        </div>
      </div>
    </div>

    <div class="details-actions">
      <router-link 
        :to="`/videos/${videoId}/edit`"
        class="important-button big"
        v-if="canEditVideo(organizationId)"
      >
        Edit Video
      </router-link>
      <button 
        class="cancel-button medium" 
        @click="goBack"
      >
        Back
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { handleApiError } from '@/scripts/shared-utils';
import { isSuperuser, canEditVideo, canShareVideo } from '@/scripts/permissions';
import { formatDate } from '@/scripts/shared-utils';
import axios from 'axios';
import ResponseMessage from '@/components/ResponseMessage.vue';
import VideoHero from '@/components/VideoHero.vue';
import VideoFileStatus from '@/components/VideoFileStatus.vue';
import ImageThumbnail from '@/components/ImageThumbnail.vue';
import {
  getVisibilityDisplay,
  getReviewStatusDisplay,
  getCategoryDisplay,
  getFormatDisplay,
  getContentRatingDisplay,
  getMotionDisplay
} from '@/scripts/shared-video';

const route = useRoute();
const router = useRouter();
const videoId = ref(null);
const videoData = ref({});
const organizationId = ref('');
const shareLink = ref(null);
const responseMessage = ref('');
const responseType = ref('');

async function fetchVideoData() {
  try {
    const response = await axios.get(`/api/videos/${videoId.value}`)
    videoData.value = response.data
    
    // Get organization ID directly from the video data
    organizationId.value = videoData.value.organization_id || ''
    
    // Check if there's an existing share link in the response
    if (videoData.value.share_links && videoData.value.share_links.length > 0) {
      shareLink.value = videoData.value.share_links[0]
    } else {
      shareLink.value = null
    }
  } catch (error) {
    handleApiError(error, 'fetching video data')
    videoData.value = {}
    organizationId.value = ''
    shareLink.value = null
  }
}

function goBack() {
  router.back();
}

async function shareVideo() {
  try {
    if (shareLink.value) {
      // Copy existing link to clipboard
      const shareUrl = `${window.location.origin}/#/share/video/${shareLink.value.short_id}`
      await navigator.clipboard.writeText(shareUrl)
      responseMessage.value = `Public link to <a href="${shareUrl}" target="_blank">${shareUrl}</a> copied to clipboard`
      responseType.value = 'success'
    } else {
      // Create new share link
      const response = await axios.post(`/api/videos/${videoId.value}/share-links`, {
        visibility: 'Public'
      })
      shareLink.value = response.data
      const shareUrl = `${window.location.origin}/#/share/video/${shareLink.value.short_id}`
      await navigator.clipboard.writeText(shareUrl)
      responseMessage.value = `Public link to <a href="${shareUrl}" target="_blank">${shareUrl}</a> copied to clipboard`
      responseType.value = 'success'
      
      // Refresh video data to get updated share_links
      await fetchVideoData()
    }
  } catch (error) {
    responseMessage.value = `Error sharing video: ${error.response?.data?.error || error.message || 'Unknown error'}`
    responseType.value = 'error'
    handleApiError(error, 'sharing video')
  }
}

async function unshareVideo() {
  try {
    if (shareLink.value) {
      await axios.delete(`/api/share-link/video/${shareLink.value.id}`)
      shareLink.value = null
      responseMessage.value = 'Video has been unshared successfully'
      responseType.value = 'success'
      
      // Refresh video data to update share_links
      await fetchVideoData()
    }
  } catch (error) {
    responseMessage.value = `Error unsharing video: ${error.response?.data?.error || error.message || 'Unknown error'}`
    responseType.value = 'error'
    handleApiError(error, 'unsharing video')
  }
}

onMounted(async () => {
  videoId.value = route.params.video_id;
  await fetchVideoData();
});
</script>

<style scoped>
@import '@/styles/shared-details.css';
</style>
